import { supabase } from '@/integrations/supabase/client';
import { formatStatusLabel } from '@/lib/statusLabels';
import { subMonths } from 'date-fns';
import { getProductionPeriod, getCurrentProductionPeriod } from '@/utils/productionPeriod';
import {
  buscarIndicadoresDesempenho,
  calcularAtingimento,
  calcularComissao,
  contarReunioesRealizadas,
} from '@/services/desempenhoDashboardService';

export interface DashboardMetricas {
  periodo: string;
  contratos_total: number;
  contratos_variacao: number;
  valor_total: number;
  valor_variacao: number;
  valor_medio: number;
  valor_medio_variacao: number;
  clientes_total: number;
  clientes_variacao: number;
  consultores_ativos: number;
  cidades_atendidas: number;
  ticket_medio: number;
  meta_cumprimento: number;
}

export interface ContratoPorPeriodo {
  periodo: string;
  contratos: number;
  contratosAnoAnterior?: number;
}

export interface DistribuicaoGeografica {
  cidade: string;
  estado: string;
  contratos: number;
  clientes: number;
  valor_total: number;
  percentual: number;
}

export interface ConsultorPerformance {
  nome: string;
  contratos: number;
  valor_total: number;
  valor_medio: number;
  ranking: number;
}

export interface TipoBem {
  tipo: string;
  contratos: number;
  valor_total: number;
  valor_medio: number;
  percentual: number;
  crescimento: number;
}

export interface UltimoContrato {
  id: number;
  contrato_numero: string;
  cliente_nome: string;
  cliente_cidade: string;
  cliente_estado: string;
  consultor_nome: string;
  bem_tipo: string;
  bem_valor: number;
  valor_parcela: number;
  valor_pago: number;
  data_criacao: string;
  status: string;
  periodo_origem: string;
}

export class SupabaseDashboardService {
  private tenantId: number;
  private cachedProductionStartDay: number | null = null;

  constructor(tenantId: number | string = 1) {
    this.tenantId = typeof tenantId === 'string' ? parseInt(tenantId) : tenantId;
  }

  private async getProductionStartDay(): Promise<number> {
    if (this.cachedProductionStartDay !== null) return this.cachedProductionStartDay;

    const { data } = await supabase
      .from('tenant_settings')
      .select('dia_inicio_producao')
      .eq('tenant_id', this.tenantId)
      .maybeSingle();

    this.cachedProductionStartDay = data?.dia_inicio_producao ?? 1;
    return this.cachedProductionStartDay;
  }

  /**
   * Busca métricas resumo do dashboard
   */
  async getMetricasResumo(startDate?: Date | string, endDate?: Date | string, periodo: string = 'thisMonth'): Promise<DashboardMetricas> {
    try {

      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const periodo_dates = await this.calcularPeriodo(periodo);
        dataInicio = periodo_dates.dataInicio;
        dataFim = periodo_dates.dataFim;
      }

      // Buscar contratos baseado no período usando created_at
      // Usar função RPC para buscar dados corretos
      const { data: contratosData } = await supabase.rpc('get_contratos_periodo', {
        tenant_id_param: this.tenantId,
        data_inicio_param: dataInicio,
        data_fim_param: dataFim
      });

      if (!contratosData) {
        return this.getMetricasVazias(periodo);
      }

      const contratosTotal = contratosData.length;
      const valorTotal = contratosData.reduce((sum, contrato) => sum + (parseFloat(contrato.preco_bem) || 0), 0);
      const valorMedio = contratosTotal > 0 ? valorTotal / contratosTotal : 0;

      // Buscar total de clientes únicos que fizeram contratos no período
      const { data: clientesData } = await supabase
        .from('contratos')
        .select('cliente_id')
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      const clientesUnicos = new Set(clientesData?.map(contrato => contrato.cliente_id) || []);
      const clientesTotal = clientesUnicos.size;

      // Buscar consultores ativos
      const { count: consultoresAtivos } = await supabase
        .from('consultores')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'ativo')
        .eq('tenant_id', this.tenantId);

      // Buscar cidades atendidas
      const { data: cidadesData } = await supabase
        .from('clientes')
        .select('cidade')
        .not('cidade', 'is', null)
        .eq('tenant_id', this.tenantId);

      const cidadesAtendidas = new Set(cidadesData?.map(c => c.cidade) || []).size;

      const refDate = dataFim.split('T')[0];
      const desempenho = await buscarIndicadoresDesempenho(this.tenantId, 'ASSOCIADO', refDate);
      const meta_cumprimento = calcularAtingimento(valorTotal, desempenho.metaValor);

      // Calcular variações (comparar com período anterior)
      const contratosVariacao = await this.calcularCrescimento(periodo);
      const valorVariacao = await this.calcularCrescimentoValor(periodo);

      const resultado = {
        periodo: this.formatarPeriodo(periodo),
        contratos_total: contratosTotal,
        contratos_variacao: contratosVariacao,
        valor_total: valorTotal,
        valor_variacao: valorVariacao,
        valor_medio: valorMedio,
        valor_medio_variacao: valorVariacao,
        clientes_total: clientesTotal || 0,
        clientes_variacao: 0,
        consultores_ativos: consultoresAtivos || 0,
        cidades_atendidas: cidadesAtendidas,
        ticket_medio: valorMedio,
        meta_cumprimento,
        desempenho,
      };

      return resultado;
    } catch (error) {
      console.error('📊 [MÉTRICAS RESUMO] Erro:', error);
      throw error;
    }
  }

  /**
   * Retorna métricas vazias quando não há dados
   */
  private getMetricasVazias(periodo: string): DashboardMetricas {
    return {
      periodo: this.formatarPeriodo(periodo),
      contratos_total: 0,
      contratos_variacao: 0,
      valor_total: 0,
      valor_variacao: 0,
      valor_medio: 0,
      valor_medio_variacao: 0,
      clientes_total: 0,
      clientes_variacao: 0,
      consultores_ativos: 0,
      cidades_atendidas: 0,
      ticket_medio: 0,
      meta_cumprimento: 0
    };
  }

  /**
   * Busca contratos por período com dados completos para gráficos
   */
  async getContratosPorPeriodoCompleto(startDate?: Date | string, endDate?: Date | string, periodo?: string): Promise<any[]> {
    try {
      const anoAtual = new Date().getFullYear();
      const anoAnterior = anoAtual - 1;

      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        // Se é string, já é ISO. Se é Date, converter para ISO
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const { dataInicio: di, dataFim: df } = await this.calcularPeriodo(periodo || 'thisMonth');
        dataInicio = di;
        dataFim = df;
      }

      // Usar consulta direta para acessar a relação correta: contratos -> bens -> grupos
      const { data, error } = await supabase
        .from('contratos')
        .select('created_at, bens(preco_bem, grupos(especie)), tenant_id')
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro na consulta Supabase:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      const meses = [
        'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
        'jul', 'ago', 'set', 'out', 'nov', 'dez'
      ];

      // Determinar estrutura de dados baseada no período
      let contratosPorMes: any[] = [];

      if (periodo === 'allTime') {
        // Para allTime, agrupar por ano e mês
        const anosUnicos = [...new Set(data.map(contrato => {
          const dataContrato = new Date(contrato.created_at);
          return dataContrato ? dataContrato.getFullYear() : null;
        }).filter(Boolean))].sort();

        // Criar estrutura para cada ano
        anosUnicos.forEach(ano => {
          meses.forEach(mes => {
            contratosPorMes.push({
              periodo: mes,
              ano: ano,
              contratos: 0,
              valor_total: 0,
              valor_medio: 0,
              imoveis: 0,
              veiculos: 0
            });
          });
        });
      } else {
        // Para outros períodos, usar lógica original
        let mesesParaIncluir = meses;
        if (periodo === 'thisMonth') {
          const mesAtual = new Date().getMonth();
          mesesParaIncluir = [meses[mesAtual]];
        } else if (periodo === 'lastMonth') {
          const mesAnterior = new Date().getMonth() - 1;
          const mesIndex = mesAnterior < 0 ? 11 : mesAnterior;
          mesesParaIncluir = [meses[mesIndex]];
        } else if (periodo === 'last3Months') {
          const mesAtual = new Date().getMonth();
          mesesParaIncluir = [];
          for (let i = 2; i >= 0; i--) {
            const mesIndex = (mesAtual - i + 12) % 12;
            mesesParaIncluir.push(meses[mesIndex]);
          }
        } else if (periodo === 'last6Months') {
          const mesAtual = new Date().getMonth();
          mesesParaIncluir = [];
          for (let i = 5; i >= 0; i--) {
            const mesIndex = (mesAtual - i + 12) % 12;
            mesesParaIncluir.push(meses[mesIndex]);
          }
        }

        contratosPorMes = mesesParaIncluir.map(mes => ({
          periodo: mes,
          contratos: 0,
          valor_total: 0,
          valor_medio: 0,
          imoveis: 0,
          veiculos: 0
        }));
      }

      // Processar dados dos contratos
      data.forEach((contrato) => {
        const dataContrato = new Date(contrato.created_at);

        if (!dataContrato) return;

        const ano = dataContrato.getFullYear();
        const mesIndex = dataContrato.getMonth();
        const mes = meses[mesIndex];

        if (periodo === 'allTime') {
          // Para allTime, processar todos os anos
          const existing = contratosPorMes.find(item => item.periodo === mes && item.ano === ano);
          if (existing) {
            existing.contratos += 1;
            existing.valor_total += parseFloat(contrato.bens?.preco_bem) || 0;

            // Contar tipos de bens baseado na espécie do grupo
            const tipoBem = contrato.bens?.grupos?.especie?.toLowerCase() || '';
            if (tipoBem.includes('imóvel') || tipoBem.includes('imovel')) {
              existing.imoveis += 1;
            } else if (tipoBem.includes('veículo') || tipoBem.includes('veiculo')) {
              existing.veiculos += 1;
            }
          }
        } else {
          // Para outros períodos, processar apenas o ano atual
          if (ano === anoAtual) {
            const existing = contratosPorMes.find(item => item.periodo === mes);
            if (existing) {
              existing.contratos += 1;
              existing.valor_total += parseFloat(contrato.bens?.preco_bem) || 0;

              // Contar tipos de bens baseado na espécie do grupo
              const tipoBem = contrato.bens?.grupos?.especie?.toLowerCase() || '';
              if (tipoBem.includes('imóvel') || tipoBem.includes('imovel')) {
                existing.imoveis += 1;
              } else if (tipoBem.includes('veículo') || tipoBem.includes('veiculo')) {
                existing.veiculos += 1;
              }
            }
          }
        }
      });

      // Calcular valores médios
      contratosPorMes.forEach(mes => {
        mes.valor_medio = mes.contratos > 0 ? mes.valor_total / mes.contratos : 0;
      });

      return contratosPorMes;
    } catch (error) {
      console.error('Erro ao buscar contratos por período completo:', error);
      throw error;
    }
  }

  /**
   * Busca contratos por período (método original)
   */
  async getContratosPorPeriodo(startDate?: Date | string, endDate?: Date | string): Promise<ContratoPorPeriodo[]> {
    try {
      let dataInicio: string;
      let dataFim: string;

      // Sempre calcular os anos para uso posterior
      const anoAtual = new Date().getFullYear();
      const anoAnterior = anoAtual - 1;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        // Fallback: Buscar contratos dos últimos 2 anos
        const diDate = new Date(`${anoAnterior}-01-01`);
        const dfDate = new Date(`${anoAtual}-12-31`);
        dataInicio = diDate.toISOString();
        dataFim = dfDate.toISOString();
      }

      const { data, error } = await supabase
        .from('contratos')
        .select('created_at, bens(preco_bem), tenant_id')
        .eq('tenant_id', this.tenantId) // Filtrar por tenant específico
        .is('deleted_at', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro na consulta Supabase:', error);
        throw error;
      }

      if (!data) {
        return [];
      }

      const meses = [
        'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
        'jul', 'ago', 'set', 'out', 'nov', 'dez'
      ];

      // Inicializar estrutura com todos os meses
      const contratosPorMes: ContratoPorPeriodo[] = meses.map(mes => ({
        periodo: mes,
        contratos: 0,
        contratosAnoAnterior: 0
      }));

      // Processar dados dos contratos - contagem por mês baseado em created_at
      data.forEach((contrato, index) => {
        // Usar created_at para determinar a data
        const dataContrato = new Date(contrato.created_at);

        if (!dataContrato || isNaN(dataContrato.getTime())) {
          console.warn(`Contrato ${index + 1} sem data válida:`, contrato);
          return;
        }

        const ano = dataContrato.getFullYear();
        const mesIndex = dataContrato.getMonth(); // 0-11

        // Mapear índice do mês para abreviação
        const mes = meses[mesIndex];

        const existing = contratosPorMes.find(item => item.periodo === mes);
        if (existing) {
          if (ano === anoAtual) {
            existing.contratos += 1;
          } else if (ano === anoAnterior) {
            existing.contratosAnoAnterior = (existing.contratosAnoAnterior || 0) + 1;
          }
        }
      });

      // Calcular total acumulado mês a mês
      let totalAcumuladoAtual = 0;
      let totalAcumuladoAnterior = 0;

      contratosPorMes.forEach((mes, index) => {
        // Acumular contratos do ano atual
        totalAcumuladoAtual += mes.contratos;
        mes.contratos = totalAcumuladoAtual;

        // Acumular contratos do ano anterior
        totalAcumuladoAnterior += (mes.contratosAnoAnterior || 0);
        mes.contratosAnoAnterior = totalAcumuladoAnterior;

      });

      return contratosPorMes;
    } catch (error) {
      console.error('Erro ao buscar contratos por período:', error);
      throw error;
    }
  }

  /**
   * Busca distribuição geográfica para um período específico
   */
  async getDistribuicaoGeografica(startDate?: Date | string, endDate?: Date | string, periodo: string = 'thisMonth'): Promise<DistribuicaoGeografica[]> {
    try {
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const periodo_dates = await this.calcularPeriodo(periodo);
        dataInicio = periodo_dates.dataInicio;
        dataFim = periodo_dates.dataFim;
      }

      const { data, error } = await supabase
        .from('contratos')
        .select(`
          created_at,
          bens(preco_bem),
          clientes(id, cidade, estado)
        `)
        .not('clientes.cidade', 'is', null)
        .not('clientes.estado', 'is', null)
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      if (error) {
        console.error('Erro na query do Supabase:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Agrupar por cidade e estado
      const distribuicao = data.reduce((acc, contrato) => {
        const cidade = contrato.clientes?.cidade;
        const estado = contrato.clientes?.estado;

        if (!cidade || !estado) return acc;

        const chave = `${cidade}-${estado}`;

        if (!acc[chave]) {
          acc[chave] = {
            cidade,
            estado,
            contratos: 0,
            clientes: new Set(),
            valor_total: 0
          };
        }

        acc[chave].contratos += 1;
        acc[chave].clientes.add(contrato.clientes?.id); // Usando ID do cliente como identificador único
        acc[chave].valor_total += parseFloat(contrato.bens?.preco_bem || '0') || 0;

        return acc;
      }, {} as Record<string, any>);

      const total = Object.values(distribuicao).reduce((sum, item) => sum + item.contratos, 0);

      const resultado = Object.values(distribuicao)
        .map(item => ({
          cidade: item.cidade || 'N/A',
          estado: item.estado || 'N/A',
          contratos: parseInt(item.contratos) || 0,
          clientes: parseInt(item.clientes.size) || 0, // Assuming item.clientes is a Set, so .size
          valor_total: parseFloat(item.valor_total) || 0,
          percentual: total > 0 ? (item.contratos / total) * 100 : 0
        }))
        .sort((a, b) => b.contratos - a.contratos);

      return resultado;
    } catch (error) {
      console.error('Erro ao buscar distribuição geográfica:', error);
      throw error;
    }
  }

  /**
   * Busca performance dos consultores para um período específico
   */
  async getConsultoresPerformance(startDate?: Date | string, endDate?: Date | string, periodo: string = 'thisMonth'): Promise<ConsultorPerformance[]> {
    try {
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const periodo_dates = await this.calcularPeriodo(periodo);
        dataInicio = periodo_dates.dataInicio;
        dataFim = periodo_dates.dataFim;
      }

      const { data, error } = await supabase
        .from('contratos')
        .select(`
          consultor_id,
          created_at,
          bens(preco_bem),
          consultores!contratos_consultor_id_fkey(nome)
        `)
        .not('consultor_id', 'is', null)
        .not('bens.preco_bem', 'is', null)
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      if (error) {
        console.error('Erro na query do Supabase:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        return [];
      }

      // Agrupar por consultor
      const performance = data.reduce((acc, contrato) => {
        const consultorId = contrato.consultor_id;
        const consultor = acc.find(c => c.consultorId === consultorId);

        if (consultor) {
          consultor.contratos += 1;
          consultor.valor += parseFloat(contrato.bens?.preco_bem) || 0;
        } else {
          acc.push({
            consultorId,
            nome: contrato.consultores?.nome || 'Consultor Desconhecido',
            contratos: 1,
            valor: parseFloat(contrato.bens?.preco_bem) || 0
          });
        }

        return acc;
      }, [] as Array<{ consultorId: number; nome: string; contratos: number; valor: number }>);

      // Calcular performance (valor por contrato)
      const performanceOrdenada = performance
        .map(consultor => ({
          nome: consultor.nome,
          contratos: consultor.contratos,
          valor_total: consultor.valor,
          valor_medio: consultor.valor / consultor.contratos
        }))
        .sort((a, b) => b.valor_total - a.valor_total);

      // Adicionar ranking após ordenação
      const resultado = performanceOrdenada.map((consultor, index) => ({
        ...consultor,
        ranking: index + 1
      }));

      return resultado;
    } catch (error) {
      console.error('Erro ao buscar performance dos consultores:', error);
      throw error;
    }
  }

  /**
   * Normaliza o nome do tipo de bem para agrupar variações similares
   */
  private normalizarTipoBem(especie: string): string {
    if (!especie) return 'Não especificado';

    const especieLower = especie.toLowerCase().trim();

    // Mapeamento de normalização
    const normalizacoes: Record<string, string> = {
      // Veículos - todas as variações
      'veículos': 'Veículos',
      'veículo': 'Veículos',
      '1-veículos': 'Veículos',
      '-veículos': 'Veículos',
      'veiculos': 'Veículos',
      'veiculo': 'Veículos',

      // Imóveis - todas as variações
      'imóveis': 'Imóveis',
      'imóvel': 'Imóveis',
      'iii-imóveis': 'Imóveis',
      'imoveis': 'Imóveis',
      'imovel': 'Imóveis',

      // Outros tipos comuns
      'serviços': 'Serviços',
      'servicos': 'Serviços',
      'eletrônicos': 'Eletrônicos',
      'eletronicos': 'Eletrônicos',
      'móveis': 'Móveis',
      'moveis': 'Móveis',
      'roupas': 'Roupas',
      'outros': 'Outros',
      'não especificado': 'Não especificado',
      'não informado': 'Não especificado',
      'xx': 'Outros'
    };

    // Buscar normalização exata
    if (normalizacoes[especieLower]) {
      return normalizacoes[especieLower];
    }

    // Buscar por padrões parciais
    if (especieLower.includes('veículo') || especieLower.includes('veiculo')) {
      return 'Veículos';
    }

    if (especieLower.includes('imóvel') || especieLower.includes('imovel')) {
      return 'Imóveis';
    }

    if (especieLower.includes('serviço') || especieLower.includes('servico')) {
      return 'Serviços';
    }

    // Se não encontrar, retorna o original capitalizado
    return especie.charAt(0).toUpperCase() + especie.slice(1).toLowerCase();
  }

  /**
   * Busca tipos de bens
   */
  async getTiposBens(startDate?: Date | string, endDate?: Date | string, periodo: string = 'thisMonth'): Promise<TipoBem[]> {
    try {

      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const periodo_dates = await this.calcularPeriodo(periodo);
        dataInicio = periodo_dates.dataInicio;
        dataFim = periodo_dates.dataFim;
      }


      const { data: contratosData, error } = await supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          bens(preco_bem, grupos(especie))
        `)
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .not('bens.preco_bem', 'is', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);


      if (error) {
        console.error('🏷️ [TIPOS BENS] Erro na query:', error);
        throw error;
      }

      if (!contratosData) {
        return [];
      }

      // Usar todos os contratos retornados (já filtrados por data)
      const contratosFiltrados = contratosData;

      if (contratosFiltrados.length === 0) {
        return [];
      }

      // Agrupar por tipo de bem normalizado
      const tipos = contratosFiltrados.reduce((acc, contrato) => {
        const especieOriginal = contrato.bens?.grupos?.especie || 'Não especificado';
        const especieNormalizada = this.normalizarTipoBem(especieOriginal);

        const existing = acc.find(t => t.tipo === especieNormalizada);

        if (existing) {
          existing.contratos += 1;
          existing.valor_total += parseFloat(contrato.bens?.preco_bem) || 0;
        } else {
          acc.push({
            tipo: especieNormalizada,
            contratos: 1,
            valor_total: parseFloat(contrato.bens?.preco_bem) || 0,
            valor_medio: 0,
            percentual: 0,
            crescimento: 0
          });
        }

        return acc;
      }, [] as TipoBem[]);

      // Calcular campos derivados
      const totalContratos = tipos.reduce((sum, tipo) => sum + tipo.contratos, 0);

      tipos.forEach(tipo => {
        tipo.valor_medio = tipo.valor_total / tipo.contratos;
        tipo.percentual = (tipo.contratos / totalContratos) * 100;
        tipo.crescimento = 0;
      });

      return tipos.sort((a, b) => b.contratos - a.contratos);
    } catch (error) {
      console.error('🏷️ [TIPOS BENS] Erro geral:', error);
      throw error;
    }
  }

  /**
   * Busca dados específicos do vendedor
   */
  async getVendedorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string): Promise<any> {
    try {
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = startDate.includes('T') ? startDate : `${startDate}T00:00:00.000Z`;
        dataFim = endDate.includes('T') ? endDate : `${endDate}T23:59:59.999Z`;
      } else {
        const periodo_calc = await this.calcularPeriodo(periodo);
        dataInicio = periodo_calc.dataInicio;
        dataFim = periodo_calc.dataFim;
      }

      // Buscar TODOS os contratos do vendedor (sem filtro de período para últimos contratos)
      let todosContratosQuery = supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          status,
          consultor_id,
          cliente_id,
          bem_id,
          grupo_id
        `)
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null);

      // Filtrar por usuário se fornecido
      if (userId) {
        todosContratosQuery = todosContratosQuery.eq('created_by', userId);
      }

      const { data: todosContratosData, error: todosContratosError } = await todosContratosQuery;

      // Buscar contratos do vendedor no período (para métricas)
      let contratosQuery = supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          status,
          consultor_id,
          cliente_id,
          bem_id,
          grupo_id
        `)
        .eq('tenant_id', this.tenantId)
        .is('deleted_at', null)
        .not('bem_id', 'is', null)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      // Filtrar por usuário se fornecido
      if (userId) {
        contratosQuery = contratosQuery.eq('created_by', userId);
      }

      const { data: contratosData, error: contratosError } = await contratosQuery;

      if (contratosError) {
        console.error('💰 [VENDEDOR STATS] Erro na query de contratos:', contratosError);
        throw contratosError;
      }

      if (todosContratosError) {
        console.error('💰 [VENDEDOR STATS] Erro na query de todos os contratos:', todosContratosError);
        throw todosContratosError;
      }

      if (!contratosData || !todosContratosData) {
        return this.getVendedorStatsVazios();
      }

      // Usar contratos filtrados por período para métricas
      const contratosFiltrados = contratosData;

      // Usar todos os contratos para últimos contratos
      const todosContratos = todosContratosData;

      // Buscar dados dos bens para calcular valor total usando SQL direto
      const bemIds = [...new Set(contratosFiltrados.map(c => c.bem_id).filter(Boolean))];
      let bensData: any[] = [];
      if (bemIds.length > 0) {
        const { data } = await supabase
          .from('bens')
          .select('id, preco_bem')
          .in('id', bemIds);
        bensData = data || [];
      }

      // Criar mapa de preços dos bens
      const precoBemMap = new Map();
      if (bensData) {
        bensData.forEach(bem => {
          precoBemMap.set(bem.id, parseFloat(bem.preco_bem) || 0);
        });
      }

      // Calcular métricas do vendedor
      const vendasRealizadas = contratosFiltrados.length;
      const valorTotalVendas = contratosFiltrados.reduce((sum, contrato) =>
        sum + (precoBemMap.get(contrato.bem_id) || 0), 0
      );

      const refDate = dataFim.split('T')[0];
      const desempenho = await buscarIndicadoresDesempenho(this.tenantId, 'LIDER_VENDAS', refDate);
      const metaMensal = desempenho.metaValor;
      const progressoMeta = calcularAtingimento(valorTotalVendas, metaMensal);
      const atingiuMeta = progressoMeta >= 100;
      const comissaoMes = calcularComissao(valorTotalVendas, desempenho, atingiuMeta);

      // Legado: metaVendas mantido como meta em R$ para compatibilidade
      const metaVendas = metaMensal;

      // Buscar contratos de hoje
      const hoje = new Date();
      const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
      const fimHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + 1);

      const vendasHoje = contratosFiltrados.filter(contrato => {
        const dataContrato = new Date(contrato.created_at);

        if (!dataContrato) return false;
        return dataContrato >= inicioHoje && dataContrato < fimHoje;
      }).length;

      // Buscar contratos da semana
      const inicioSemana = new Date(hoje);
      inicioSemana.setDate(hoje.getDate() - hoje.getDay());
      inicioSemana.setHours(0, 0, 0, 0);

      const vendasSemana = contratosFiltrados.filter(contrato => {
        const dataContrato = new Date(contrato.created_at);

        if (!dataContrato) return false;
        return dataContrato >= inicioSemana;
      }).length;

      // Buscar dados dos clientes e bens para últimos contratos
      const clienteIds = [...new Set(todosContratos.map(c => c.cliente_id).filter(Boolean))];
      const bemIdsUltimos = [...new Set(todosContratos.map(c => c.bem_id).filter(Boolean))];

      const { data: clientesData } = await supabase
        .from('clientes')
        .select('id, nome')
        .in('id', clienteIds);

      let bensUltimosData: any[] = [];
      if (bemIdsUltimos.length > 0) {
        const { data } = await supabase
          .from('bens')
          .select('id, preco_bem')
          .in('id', bemIdsUltimos);
        bensUltimosData = data || [];
      }

      // Criar mapas para lookup
      const clienteMap = new Map();
      if (clientesData) {
        clientesData.forEach(cliente => {
          clienteMap.set(cliente.id, cliente.nome);
        });
      }

      const precoBemUltimosMap = new Map();
      if (bensUltimosData) {
        bensUltimosData.forEach(bem => {
          precoBemUltimosMap.set(bem.id, parseFloat(bem.preco_bem) || 0);
        });
      }

      // Buscar últimos contratos (usar todos os contratos, não apenas do período)
      const ultimosContratos = todosContratos
        .filter(contrato => contrato.bem_id && precoBemUltimosMap.get(contrato.bem_id)) // Filtrar apenas contratos com preço do bem
        .sort((a, b) => {
          const dataA = new Date(a.created_at);
          const dataB = new Date(b.created_at);
          return dataB.getTime() - dataA.getTime();
        })
        .slice(0, 5)
        .map(contrato => ({
          id: contrato.id,
          cliente: clienteMap.get(contrato.cliente_id) || 'Cliente não informado',
          valor: precoBemUltimosMap.get(contrato.bem_id) || 0,
          status: contrato.status || 'Aprovado',
          data: contrato.created_at.split('T')[0]
        }));

      // Calcular desempenho mensal (últimos 6 meses)
      const desempenhoMensal = this.calcularDesempenhoMensal(contratosData);

      // Calcular contratos pendentes (Em Andamento + Enviado para Relacionador)
      const contratosPendentes = contratosFiltrados.filter(c =>
        c.status === 'em_andamento' || c.status === 'enviado_para_relacionador'
      ).length;

      const contratosAtivos = contratosFiltrados.filter(
        c => c.status === 'ATIVO' || c.status === 'ativo' || c.status === 'FINALIZADO'
      ).length;
      const taxaConversao =
        vendasRealizadas > 0 ? Math.round((contratosAtivos / vendasRealizadas) * 100) : 0;

      const clientesUnicos = new Set(contratosFiltrados.map(c => c.cliente_id).filter(Boolean)).size;

      return {
        vendasRealizadas,
        metaVendas,
        metaMensal,
        clientesProspectados: clientesUnicos,
        comissaoMes,
        progressoMeta,
        desempenho,
        vendasHoje,
        vendasSemana,
        vendasMes: vendasRealizadas,
        valorTotalVendas,
        ultimosContratos,
        desempenhoMensal,
        contratosPendentes,
        taxaConversao,
        comissoesPendentes: comissaoMes * 0.3, // 30% pendente
        comissoesPagas: comissaoMes * 0.7, // 70% pago
        clientesAtivos: clientesUnicos
      };

    } catch (error) {
      console.error('💰 [VENDEDOR STATS] Erro geral:', error);
      throw error;
    }
  }

  /**
   * Busca líderes do mesmo time que um relacionador
   */
  private async getLideresDoMesmoTime(relacionadorUserId: string): Promise<string[]> {
    try {
      // Buscar o consultor relacionador
      const { data: relacionador, error: relacionadorError } = await supabase
        .from('consultores')
        .select('id, supervisor_id')
        .eq('user_id', relacionadorUserId)
        .eq('tipo', 'relacionador')
        .maybeSingle();

      if (relacionadorError) {
        console.error('Erro ao buscar relacionador:', relacionadorError);
        return [];
      }

      if (!relacionador) {
        return [];
      }

      // Buscar todos os líderes que têm o mesmo supervisor
      const { data: lideres, error: lideresError } = await supabase
        .from('consultores')
        .select('user_id')
        .eq('supervisor_id', (relacionador as any).supervisor_id)
        .eq('tipo', 'lider')
        .eq('status', 'ativo');

      if (lideresError) {
        console.error('Erro ao buscar líderes do time:', lideresError);
        return [];
      }

      // Retornar array com os UUIDs dos líderes + o próprio relacionador
      const userIds = (lideres as any[]).map(l => l.user_id).filter(Boolean);
      userIds.push(relacionadorUserId); // Incluir o próprio relacionador

      return userIds;
    } catch (error) {
      console.error('Erro ao buscar líderes do mesmo time:', error);
      return [];
    }
  }

  /**
   * Busca dados específicos do relacionador
   */
  async getRelacionadorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string): Promise<any> {
    try {
      // Calcular datas baseado no período ou usar datas passadas
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = startDate;
        dataFim = endDate;
      } else {
        const periodo_calc = await this.calcularPeriodo(periodo);
        dataInicio = periodo_calc.dataInicio;
        dataFim = periodo_calc.dataFim;
      }

      // Buscar todos os contratos no período (sem filtro de usuário inicial)
      let contratosQuery = supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          status,
          consultor_id,
          cliente_id,
          bem_id,
          grupo_id,
          created_by
        `)
        .eq('tenant_id', this.tenantId)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      const { data: todosContratos, error: contratosError } = await contratosQuery;

      if (contratosError) {
        console.error('🔗 [RELACIONADOR STATS] Erro na query de contratos:', contratosError);
        throw contratosError;
      }

      const todosContratosData = todosContratos || [];

      // Aplicar filtragem específica para relacionadores
      let contratos = todosContratosData;

      if (userId) {
        // Buscar líderes do mesmo time do relacionador
        const lideresDoTime = await this.getLideresDoMesmoTime(userId);

        // Filtrar contratos: apenas os criados pelo próprio relacionador OU pelos líderes do seu time com status específicos
        contratos = todosContratosData.filter((contrato: any) => {
          // Se foi criado pelo próprio relacionador, sempre mostrar
          if (contrato.created_by === userId) {
            return true;
          }

          // Se foi criado por um líder do time, só mostrar se estiver "enviado para relacionador" ou "ativo"
          if (lideresDoTime.includes(contrato.created_by)) {
            return contrato.status === 'enviado_para_relacionador' ||
              contrato.status === 'ENVIADO_PARA_RELACIONADOR' ||
              contrato.status === 'ativo' ||
              contrato.status === 'ATIVO';
          }

          return false;
        });
      }

      // Buscar dados dos bens para calcular valor total
      const bemIds = [...new Set(contratos.map(c => c.bem_id).filter(Boolean))];
      let bensData: any[] = [];
      if (bemIds.length > 0) {
        const { data } = await supabase
          .from('bens')
          .select('id, preco_bem')
          .in('id', bemIds);
        bensData = data || [];
      }

      // Criar mapa de preços dos bens
      const precoBemMap = new Map();
      bensData.forEach(bem => {
        precoBemMap.set(bem.id, parseFloat(bem.preco_bem) || 0);
      });

      const vendasFinalizadas = contratos.filter(c => c.status === 'ATIVO' || c.status === 'FINALIZADO').length;
      const valorTotalVendas = contratos.reduce((sum, contrato) => {
        const precoBem = precoBemMap.get(contrato.bem_id) || 0;
        return sum + precoBem;
      }, 0);

      // Buscar clientes únicos atendidos - usar mesma lógica da página de clientes
      let clientesAtendidos = 0;

      if (userId) {
        // Para relacionadores, buscar todos os clientes que podem ver (mesmo lógica da página de clientes)
        const lideresDoTime = await this.getLideresDoMesmoTime(userId);

        // Buscar contratos que o relacionador pode ver (próprios + enviados pelos líderes)
        const { data: contratosPermitidos } = await supabase
          .from('contratos')
          .select('cliente_id, created_by, status')
          .or(`created_by.eq.${userId},and(created_by.in.(${lideresDoTime.join(',')}),or(status.eq.enviado_para_relacionador,status.eq.ENVIADO_PARA_RELACIONADOR))`);

        // Extrair IDs dos clientes que têm contratos permitidos
        const clientesComContratosPermitidosIds = new Set(
          contratosPermitidos?.map(c => c.cliente_id).filter(Boolean) || []
        );

        // Buscar clientes criados pelo relacionador
        const { data: clientesProprios } = await supabase
          .from('clientes')
          .select('id')
          .eq('created_by', userId)
          .eq('tenant_id', this.tenantId);

        // Contar clientes únicos: próprios + com contratos permitidos
        const clientesPropriosIds = new Set(clientesProprios?.map(c => parseInt(c.id)) || []);
        const todosClientesUnicos = new Set([...clientesPropriosIds, ...clientesComContratosPermitidosIds]);

        clientesAtendidos = todosClientesUnicos.size;
      } else {
        // Para outros usuários, usar lógica original
        const clientesUnicos = new Set(contratos.map(contrato => contrato.cliente_id));
        clientesAtendidos = clientesUnicos.size;
      }

      // Buscar contratos por status
      const contratosPorStatus = contratos.reduce((acc, contrato) => {
        const status = contrato.status || 'PENDENTE';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const totalContratos = contratos.length;
      const taxaFechamento = totalContratos > 0 ? (vendasFinalizadas / totalContratos) * 100 : 0;
      const ticketMedio = vendasFinalizadas > 0 ? valorTotalVendas / vendasFinalizadas : 0;

      const refDate = dataFim.split('T')[0];
      const desempenho = await buscarIndicadoresDesempenho(this.tenantId, 'RELACIONADOR', refDate);

      let consultorId: number | undefined;
      if (userId) {
        const { data: consultor } = await supabase
          .from('consultores')
          .select('id')
          .eq('user_id', userId)
          .eq('tipo', 'relacionador')
          .maybeSingle();
        consultorId = consultor?.id;
      }

      const reunioesRealizadas = await contarReunioesRealizadas(
        this.tenantId,
        dataInicio.split('T')[0],
        dataFim.split('T')[0],
        consultorId
      );

      const metaReunioes = desempenho.metaReunioes;
      const metaVendas = metaReunioes;
      const metaFaturamento = desempenho.metaValor;
      const progressoReunioes = calcularAtingimento(reunioesRealizadas, metaReunioes);
      const progressoFaturamento = calcularAtingimento(valorTotalVendas, metaFaturamento);
      const metaMensalAlcancada = progressoReunioes;
      const atingiuMeta = progressoReunioes >= 100 || (metaFaturamento > 0 && progressoFaturamento >= 100);
      const comissoesReceber = calcularComissao(valorTotalVendas, desempenho, atingiuMeta);
      const comissoesPagas = comissoesReceber * 0.6;

      // Vendas por mês (últimos 6 meses)
      const vendasPorMes = this.calcularDesempenhoMensal(contratos);

      // Mapeamento de status para labels mais amigáveis
      const statusLabelMap: Record<string, string> = {
        'ATIVO': 'Ativos',
        'ativo': 'Ativos',
        'em_andamento': 'Negociando',
        'EM_ANDAMENTO': 'Negociando',
        'enviado_para_relacionador': 'Recebidos',
        'ENVIADO_PARA_RELACIONADOR': 'Recebidos',
        'PENDENTE': 'Pendentes',
        'pendente': 'Pendentes',
        'FINALIZADO': 'Finalizados',
        'finalizado': 'Finalizados',
        'CANCELADO': 'Cancelados',
        'cancelado': 'Cancelados',
        'EXPIRADO': 'Expirados',
        'expirado': 'Expirados'
      };

      // Contratos por status para gráfico - unificar "ativo" e "ATIVO" e aplicar labels amigáveis
      const contratosStatusChart = Object.entries(contratosPorStatus).reduce((acc, [status, value]) => {
        // Unificar status "ativo" e "ATIVO" em um só
        const normalizedStatus = status.toLowerCase() === 'ativo' ? 'ATIVO' : status;

        // Aplicar label amigável ou usar o status original se não houver mapeamento
        const friendlyLabel = statusLabelMap[normalizedStatus] || statusLabelMap[status] || formatStatusLabel(status);

        if (acc.find(item => item.name === friendlyLabel)) {
          // Se já existe, somar os valores
          const existingItem = acc.find(item => item.name === friendlyLabel);
          if (existingItem) {
            existingItem.value += value;
          }
        } else {
          // Se não existe, adicionar novo
          acc.push({
            name: friendlyLabel,
            value: value
          });
        }

        return acc;
      }, [] as Array<{ name: string, value: number }>);

      return {
        vendasFinalizadas,
        clientesAtendidos,
        taxaFechamento: Math.round(taxaFechamento * 100) / 100,
        metaMensalAlcancada: Math.round(metaMensalAlcancada * 100) / 100,
        faturamentoGerado: valorTotalVendas,
        comissoesReceber,
        comissoesPagas,
        ticketMedio,
        metaVendas,
        metaReunioes,
        reunioesRealizadas,
        progressoReunioes,
        progressoFaturamento,
        metaFaturamento,
        desempenho,
        vendasPorMes,
        contratosPorStatus: contratosStatusChart,
        totalContratos,
        contratosPendentes: contratosPorStatus['PENDENTE'] || 0,
        contratosAtivos: contratosPorStatus['ATIVO'] || 0
      };

    } catch (error) {
      console.error('🔗 [RELACIONADOR STATS] Erro geral:', error);
      throw error;
    }
  }

  /**
   * Retorna dados vazios para vendedor quando não há dados
   */
  private getVendedorStatsVazios() {
    return {
      vendasRealizadas: 0,
      metaVendas: 0,
      metaMensal: 0,
      clientesProspectados: 0,
      comissaoMes: 0,
      progressoMeta: 0,
      vendasHoje: 0,
      vendasSemana: 0,
      vendasMes: 0,
      valorTotalVendas: 0,
      ultimosContratos: [],
      desempenhoMensal: [],
      contratosPendentes: 0,
      comissoesPendentes: 0,
      comissoesPagas: 0,
      clientesAtivos: 0
    };
  }

  /**
   * Calcula desempenho mensal dos últimos 6 meses
   */
  private calcularDesempenhoMensal(contratosData: any[]): Array<{ mes: string; vendas: number }> {
    const meses = [];
    const hoje = new Date();

    for (let i = 5; i >= 0; i--) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
      const proximoMes = new Date(hoje.getFullYear(), hoje.getMonth() - i + 1, 1);

      const vendasMes = contratosData.filter(contrato => {
        const dataContrato = new Date(contrato.created_at);

        if (!dataContrato) return false;
        return dataContrato >= data && dataContrato < proximoMes;
      }).length;

      meses.push({
        mes: data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
        vendas: vendasMes
      });
    }

    return meses;
  }

  /**
   * Busca dados históricos detalhados do vendedor
   */
  async getVendedorHistorico(periodo: string = '12months'): Promise<any[]> {
    try {
      // Buscar contratos dos últimos 12 meses
      const dataInicio = new Date();
      dataInicio.setMonth(dataInicio.getMonth() - 12);

      const { data: contratosData, error } = await supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          status,
          consultor_id,
          bens(preco_bem, grupos(especie))
        `)
        .eq('tenant_id', this.tenantId)
        .not('bens.preco_bem', 'is', null)
        .gte('created_at', dataInicio.toISOString());

      if (error) {
        console.error('📊 [VENDEDOR HISTORICO] Erro na query:', error);
        throw error;
      }

      if (!contratosData) {
        return [];
      }

      const meses = [];
      const hoje = new Date();
      const mesesParaBuscar = periodo === '12months' ? 12 : 6;

      for (let i = mesesParaBuscar - 1; i >= 0; i--) {
        const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
        const proximoMes = new Date(hoje.getFullYear(), hoje.getMonth() - i + 1, 1);

        const contratosMes = contratosData.filter(contrato => {
          const dataContrato = new Date(contrato.created_at);

          if (!dataContrato) return false;
          return dataContrato >= data && dataContrato < proximoMes;
        });

        const vendasMes = contratosMes.length;
        const valorTotal = contratosMes.reduce((sum, contrato) =>
          sum + (parseFloat(contrato.preco_bem) || 0), 0
        );
        const comissaoMes = valorTotal * 0.05; // 5% de comissão
        const clientesUnicos = new Set(contratosMes.map(c => c.consultor_id)).size;

        meses.push({
          mes: data.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }),
          vendas: vendasMes,
          valor: valorTotal,
          comissao: comissaoMes,
          clientes: clientesUnicos,
          meta: 50, // Meta padrão
          percentualMeta: (vendasMes / 50) * 100
        });
      }

      return meses;
    } catch (error) {
      console.error('📊 [VENDEDOR HISTORICO] Erro geral:', error);
      throw error;
    }
  }

  /**
   * Busca performance das empresas (tenants) para um período específico
   */
  async getTenantsPerformance(startDate?: Date | string, endDate?: Date | string, periodo: string = 'thisMonth'): Promise<any[]> {
    try {
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = typeof startDate === 'string' ? startDate : startDate.toISOString();
        dataFim = typeof endDate === 'string' ? endDate : endDate.toISOString();
      } else {
        const periodo_dates = await this.calcularPeriodo(periodo);
        dataInicio = periodo_dates.dataInicio;
        dataFim = periodo_dates.dataFim;
      }

      // Buscar todos os tenants
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, nome, created_at')
        .eq('status', 'ATIVO')
        .order('created_at', { ascending: true });

      if (tenantsError) {
        console.error('Erro ao buscar tenants:', tenantsError);
        throw tenantsError;
      }

      if (!tenants || tenants.length === 0) {
        return [];
      }

      // Buscar dados de performance para cada tenant no período selecionado
      const tenantsWithPerformance = await Promise.all(
        tenants.map(async (tenant) => {
          try {
            // Buscar contratos do tenant no período selecionado
            const { data: contratosData, error: contratosError } = await supabase
              .from('contratos')
              .select('id, created_at, bens(preco_bem), cliente_id')
              .eq('tenant_id', tenant.id)
              .gte('created_at', dataInicio)
              .lte('created_at', dataFim);

            if (contratosError) {
              console.error(`Erro ao buscar contratos do tenant ${tenant.id}:`, contratosError);
              return null;
            }

            const contratos = contratosData || [];
            const contratosTotal = contratos.length;

            // Se não há contratos no período, retornar dados zerados
            if (contratosTotal === 0) {
              return {
                tenant_id: tenant.id,
                tenant_nome: tenant.nome,
                contratos_total: 0,
                valor_total: 0,
                valor_medio: 0,
                clientes_unicos: 0,
                consultores_ativos: 0,
                ranking: 0,
                performance_score: 0,
                crescimento_mensal: 0
              };
            }

            // Calcular valor total e médio
            const valorTotal = contratos.reduce((sum, contrato) => {
              return sum + (parseFloat(contrato.bens?.preco_bem) || 0);
            }, 0);

            const valorMedio = contratosTotal > 0 ? valorTotal / contratosTotal : 0;

            // Contar clientes únicos no período
            const clientesUnicos = new Set(contratos.map(c => c.cliente_id)).size;

            // Buscar consultores ativos
            const { data: consultoresData } = await supabase
              .from('consultores')
              .select('id')
              .eq('tenant_id', tenant.id);

            const consultoresAtivos = consultoresData?.length || 0;

            // Calcular crescimento comparando com período anterior
            const crescimentoMensal = await this.calcularCrescimentoTenant(tenant.id, dataInicio, dataFim);

            // Calcular performance score (0-100) baseado no período
            const performanceScore = Math.min(100, Math.max(0,
              (contratosTotal * 0.4) +
              (valorTotal / 100000 * 0.3) +
              (clientesUnicos * 0.2) +
              (Math.max(0, crescimentoMensal) * 0.1)
            ));

            return {
              tenant_id: tenant.id,
              tenant_nome: tenant.nome,
              contratos_total: contratosTotal,
              valor_total: valorTotal,
              valor_medio: valorMedio,
              clientes_unicos: clientesUnicos,
              consultores_ativos: consultoresAtivos,
              ranking: 0, // Será calculado depois
              performance_score: Math.round(performanceScore),
              crescimento_mensal: Math.round(crescimentoMensal * 10) / 10
            };
          } catch (error) {
            console.error(`Erro ao processar tenant ${tenant.id}:`, error);
            return null;
          }
        })
      );

      // Filtrar tenants válidos e ordenar por performance
      const validTenants = tenantsWithPerformance
        .filter(tenant => tenant !== null)
        .sort((a, b) => b.performance_score - a.performance_score);

      // Atribuir rankings
      validTenants.forEach((tenant, index) => {
        tenant.ranking = index + 1;
      });

      return validTenants;

    } catch (error) {
      console.error('🏢 [PERFORMANCE EMPRESAS] Erro:', error);
      throw error;
    }
  }

  /**
   * Calcula crescimento de um tenant comparando com período anterior
   */
  private async calcularCrescimentoTenant(tenantId: number, dataInicio: string | Date, dataFim: string | Date): Promise<number> {
    try {
      // Converter para Date se necessário
      const dataInicioDate = typeof dataInicio === 'string' ? new Date(dataInicio) : dataInicio;
      const dataFimDate = typeof dataFim === 'string' ? new Date(dataFim) : dataFim;

      // Verificar se as datas são válidas
      if (!dataInicioDate || !dataFimDate || isNaN(dataInicioDate.getTime()) || isNaN(dataFimDate.getTime())) {
        console.error('Datas inválidas para calcular crescimento:', { dataInicio, dataFim });
        return 0;
      }

      // Calcular período anterior com mesma duração
      const duracao = dataFimDate.getTime() - dataInicioDate.getTime();
      const dataAnteriorFim = new Date(dataInicioDate.getTime() - 1);
      const dataAnteriorInicio = new Date(dataAnteriorFim.getTime() - duracao);

      // Buscar contratos do período atual
      const { data: contratosAtual } = await supabase
        .from('contratos')
        .select('id')
        .eq('tenant_id', tenantId)
        .gte('created_at', dataInicioDate.toISOString())
        .lte('created_at', dataFimDate.toISOString());

      // Buscar contratos do período anterior
      const { data: contratosAnterior } = await supabase
        .from('contratos')
        .select('id')
        .eq('tenant_id', tenantId)
        .gte('created_at', dataAnteriorInicio.toISOString())
        .lte('created_at', dataAnteriorFim.toISOString());

      const contratosAtualCount = contratosAtual?.length || 0;
      const contratosAnteriorCount = contratosAnterior?.length || 0;

      if (contratosAnteriorCount === 0) {
        return contratosAtualCount > 0 ? 100 : 0;
      }

      return ((contratosAtualCount - contratosAnteriorCount) / contratosAnteriorCount) * 100;
    } catch (error) {
      console.error('Erro ao calcular crescimento do tenant:', error);
      return 0;
    }
  }

  /**
   * Busca últimos contratos
   */
  async getUltimosContratos(limit: number = 20): Promise<UltimoContrato[]> {
    try {
      const { data, error } = await supabase
        .from('contratos')
        .select(`
          id,
          contrato_numero,
          valor_parcela,
          total_pago,
          created_at,
          status,
          planilha_origem,
          clientes(nome, cidade, estado),
          consultores!contratos_consultor_id_fkey(nome),
          bens(tipo_bem, preco_bem)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Erro na consulta Supabase:', error);
        throw error;
      }

      if (!data) return [];

      return data.map(contrato => ({
        id: contrato.id,
        contrato_numero: contrato.contrato_numero || `CT-${contrato.id}`,
        cliente_nome: contrato.clientes?.nome || 'Cliente Desconhecido',
        cliente_cidade: contrato.clientes?.cidade || 'N/A',
        cliente_estado: contrato.clientes?.estado || 'N/A',
        consultor_nome: contrato.consultores?.nome || 'Consultor Desconhecido',
        bem_tipo: contrato.bens?.tipo_bem || 'N/A',
        bem_valor: contrato.bens?.preco_bem || 0,
        valor_parcela: contrato.valor_parcela || 0,
        valor_pago: contrato.total_pago || 0,
        data_criacao: contrato.created_at,
        status: contrato.status || 'N/A',
        periodo_origem: contrato.planilha_origem || 'Atual'
      }));
    } catch (error) {
      console.error('Erro ao buscar últimos contratos:', error);
      throw error;
    }
  }

  /**
   * Calcula período baseado na string fornecida (respeita dia_inicio_producao do tenant)
   */
  private async calcularPeriodo(periodo: string): Promise<{ dataInicio: string; dataFim: string }> {
    const agora = new Date();
    const startDay = await this.getProductionStartDay();

    let dataInicio: Date;
    let dataFim: Date;

    switch (periodo) {
      case 'today':
        dataInicio = new Date(agora);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'yesterday':
        dataInicio = new Date(agora);
        dataInicio.setDate(dataInicio.getDate() - 1);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setDate(dataFim.getDate() - 1);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'last7days':
        dataInicio = new Date(agora);
        dataInicio.setDate(dataInicio.getDate() - 7);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'last30days':
        dataInicio = new Date(agora);
        dataInicio.setDate(dataInicio.getDate() - 30);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'last90days':
        dataInicio = new Date(agora);
        dataInicio.setDate(dataInicio.getDate() - 90);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'thisMonth': {
        const current = getCurrentProductionPeriod(startDay);
        dataInicio = current.startDate;
        dataFim = current.endDate;
        break;
      }
      case 'lastMonth': {
        const current = getCurrentProductionPeriod(startDay);
        const lastMonthDate = subMonths(current.endDate, 1);
        const period = getProductionPeriod(lastMonthDate.getMonth(), lastMonthDate.getFullYear(), startDay);
        dataInicio = period.startDate;
        dataFim = period.endDate;
        break;
      }
      case 'thisQuarter':
        const currentQuarter = Math.floor(agora.getMonth() / 3);
        dataInicio = new Date(agora.getFullYear(), currentQuarter * 3, 1);
        dataFim = new Date(agora.getFullYear(), currentQuarter * 3 + 3, 0);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'lastQuarter':
        const lastQuarter = Math.floor(agora.getMonth() / 3) - 1;
        if (lastQuarter < 0) {
          dataInicio = new Date(agora.getFullYear() - 1, 9, 1);
          dataFim = new Date(agora.getFullYear() - 1, 11, 31);
        } else {
          dataInicio = new Date(agora.getFullYear(), lastQuarter * 3, 1);
          dataFim = new Date(agora.getFullYear(), lastQuarter * 3 + 3, 0);
        }
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'thisYear':
        dataInicio = new Date(agora.getFullYear(), 0, 1);
        dataFim = new Date(agora.getFullYear(), 11, 31);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'lastYear':
        dataInicio = new Date(agora.getFullYear() - 1, 0, 1);
        dataFim = new Date(agora.getFullYear() - 1, 11, 31);
        dataFim.setHours(23, 59, 59, 999);
        break;
      case 'allTime':
        // Dados históricos completos - desde 2020 até agora
        dataInicio = new Date(2020, 0, 1);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
        break;
      default:
        // Fallback para período padrão (últimos 30 dias)
        dataInicio = new Date(agora);
        dataInicio.setDate(dataInicio.getDate() - 30);
        dataInicio.setHours(0, 0, 0, 0);
        dataFim = new Date(agora);
        dataFim.setHours(23, 59, 59, 999);
    }

    return {
      dataInicio: dataInicio.toISOString(),
      dataFim: dataFim.toISOString()
    };
  }

  /**
   * Calcula crescimento comparando com período anterior
   */
  private async calcularCrescimento(periodo: string): Promise<number> {
    try {
      const { dataInicio: atualInicio, dataFim: atualFim } = await this.calcularPeriodo(periodo);

      // Calcular período anterior
      const dataAtualInicio = new Date(atualInicio);
      const dataAnteriorFim = new Date(dataAtualInicio);
      dataAnteriorFim.setDate(dataAnteriorFim.getDate() - 1);

      const dataAnteriorInicio = new Date(dataAtualInicio);
      const diasPeriodo = Math.ceil((new Date(atualFim).getTime() - dataAtualInicio.getTime()) / (1000 * 60 * 60 * 24));
      dataAnteriorInicio.setDate(dataAnteriorInicio.getDate() - diasPeriodo);

      // Buscar contratos dos períodos atual e anterior
      const { data: contratosAtual } = await supabase
        .from('contratos')
        .select('created_at')
        .eq('tenant_id', this.tenantId)
        .gte('created_at', atualInicio)
        .lte('created_at', atualFim);

      const { data: contratosAnteriorData } = await supabase
        .from('contratos')
        .select('created_at')
        .eq('tenant_id', this.tenantId)
        .gte('created_at', dataAnteriorInicio.toISOString())
        .lte('created_at', dataAnteriorFim.toISOString());

      const contratosAtualCount = contratosAtual?.length || 0;
      const contratosAnteriorCount = contratosAnteriorData?.length || 0;

      if (contratosAnteriorCount === 0) return 0;

      return ((contratosAtualCount - contratosAnteriorCount) / contratosAnteriorCount) * 100;
    } catch (error) {
      console.error('Erro ao calcular crescimento:', error);
      return 0;
    }
  }

  /**
   * Calcula crescimento do valor comparando com período anterior
   */
  private async calcularCrescimentoValor(periodo: string): Promise<number> {
    try {
      const { dataInicio: atualInicio, dataFim: atualFim } = await this.calcularPeriodo(periodo);

      // Calcular período anterior
      const dataAtualInicio = new Date(atualInicio);
      const dataAnteriorFim = new Date(dataAtualInicio);
      dataAnteriorFim.setDate(dataAnteriorFim.getDate() - 1);

      const dataAnteriorInicio = new Date(dataAtualInicio);
      const diasPeriodo = Math.ceil((new Date(atualFim).getTime() - dataAtualInicio.getTime()) / (1000 * 60 * 60 * 24));
      dataAnteriorInicio.setDate(dataAnteriorInicio.getDate() - diasPeriodo);

      // Buscar contratos dos períodos atual e anterior
      const { data: contratosAtual } = await supabase.rpc('get_contratos_periodo', {
        tenant_id_param: this.tenantId,
        data_inicio_param: atualInicio,
        data_fim_param: atualFim
      });

      const { data: contratosAnterior } = await supabase.rpc('get_contratos_periodo', {
        tenant_id_param: this.tenantId,
        data_inicio_param: dataAnteriorInicio.toISOString(),
        data_fim_param: dataAnteriorFim.toISOString()
      });

      const valorAtual = contratosAtual?.reduce((sum, contrato) => sum + (parseFloat(contrato.preco_bem) || 0), 0) || 0;
      const valorAnterior = contratosAnterior?.reduce((sum, contrato) => sum + (parseFloat(contrato.preco_bem) || 0), 0) || 0;

      if (valorAnterior === 0) return 0;

      return ((valorAtual - valorAnterior) / valorAnterior) * 100;
    } catch (error) {
      console.error('Erro ao calcular crescimento do valor:', error);
      return 0;
    }
  }

  /**
   * Formata o período para exibição
   */
  private formatarPeriodo(periodo: string): string {
    const agora = new Date();

    switch (periodo) {
      case 'today':
        return 'Hoje';
      case 'yesterday':
        return 'Ontem';
      case 'last7days':
        return 'Últimos 7 dias';
      case 'last30days':
        return 'Últimos 30 dias';
      case 'last90days':
        return 'Últimos 90 dias';
      case 'thisMonth':
        return agora.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      case 'lastMonth':
        const lastMonth = new Date(agora.getFullYear(), agora.getMonth() - 1);
        return lastMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      case 'thisQuarter':
        const currentQuarter = Math.floor(agora.getMonth() / 3) + 1;
        return `${currentQuarter}º trimestre de ${agora.getFullYear()}`;
      case 'lastQuarter':
        const lastQuarter = Math.floor(agora.getMonth() / 3);
        const year = lastQuarter === 0 ? agora.getFullYear() - 1 : agora.getFullYear();
        return `${lastQuarter === 0 ? 4 : lastQuarter}º trimestre de ${year}`;
      case 'thisYear':
        return agora.getFullYear().toString();
      case 'lastYear':
        return (agora.getFullYear() - 1).toString();
      case 'allTime':
        return 'Todos os dados (2020-2025)';
      default:
        return 'Período selecionado';
    }
  }

  /**
   * Busca contratos por categoria (tipo de bem)
   */
  async getContratosPorCategoria(tipoBem: string, periodo: string = 'thisMonth'): Promise<UltimoContrato[]> {
    try {
      // Calcular datas baseado no período
      const { dataInicio, dataFim } = await this.calcularPeriodo(periodo);

      // Mapear categorias do gráfico para as espécies na tabela grupos
      const categoriaMapping: { [key: string]: string | null } = {
        'Veículos': 'Veículos',
        'Veículo': 'Veículos',
        'Imóveis': 'Imóveis',
        'Imóvel': 'Imóveis',
        'Não especificado': null,
        'Não Informado': null,
        'Outros': 'Outros',
        'Serviços': 'Serviços',
        'Eletrônicos': 'Eletrônicos',
        'Móveis': 'Móveis',
        'Roupas': 'Roupas'
      };

      // Obter a espécie correspondente
      const especieParaBuscar = categoriaMapping[tipoBem] !== undefined ? categoriaMapping[tipoBem] : tipoBem;

      let query = supabase
        .from('contratos')
        .select(`
          id,
          contrato_numero,
          clientes(nome, cidade, estado),
          consultores!contratos_consultor_id_fkey(nome),
          bens(tipo_bem, preco_bem),
          grupos(especie),
          valor_parcela,
          total_pago,
          created_at,
          status
        `)
        .eq('tenant_id', this.tenantId)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      // Se a espécie for null, usar filtro IS NULL, senão usar eq
      if (especieParaBuscar === null) {
        query = query.is('grupos.especie', null);
      } else {
        query = query.eq('grupos.especie', especieParaBuscar);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Erro na query do Supabase:', error);
        throw error;
      }

      if (!data) return [];

      // Usar todos os contratos retornados (já filtrados por data)
      const contratosFiltrados = data;

      const contratosMapeados = contratosFiltrados.map(contrato => ({
        id: contrato.id,
        contrato_numero: contrato.contrato_numero || `CON-${contrato.id}`,
        cliente_nome: contrato.clientes?.nome || 'Cliente não informado',
        cliente_cidade: contrato.clientes?.cidade || 'Cidade não informada',
        cliente_estado: contrato.clientes?.estado || 'Estado não informado',
        consultor_nome: contrato.consultores?.nome || 'Consultor não informado',
        bem_tipo: contrato.grupos?.especie || 'Não especificado',
        bem_valor: parseFloat(contrato.bens?.preco_bem) || 0,
        valor_parcela: parseFloat(contrato.valor_parcela) || 0,
        valor_pago: parseFloat(contrato.total_pago) || 0,
        data_criacao: contrato.created_at,
        status: contrato.status || 'pendente',
        periodo_origem: 'Sistema'
      }));

      return contratosMapeados;
    } catch (error) {
      console.error('Erro ao buscar contratos por categoria:', error);
      throw error;
    }
  }

  /**
   * Busca dados específicos do supervisor
   */
  async getSupervisorStats(periodo: string = 'thisMonth', userId?: string, startDate?: string, endDate?: string): Promise<any> {
    try {
      // Calcular datas baseado no período ou usar datas passadas
      let dataInicio: string;
      let dataFim: string;

      if (startDate && endDate) {
        dataInicio = startDate;
        dataFim = endDate;
      } else {
        const periodo_calc = await this.calcularPeriodo(periodo);
        dataInicio = periodo_calc.dataInicio;
        dataFim = periodo_calc.dataFim;
      }

      // Buscar membros da equipe do supervisor
      const membrosDoTime = await this.getMembrosDoTimeSupervisor(userId);

      // Buscar todos os contratos criados pelos membros da equipe no período
      const { data: contratosData, error: contratosError } = await supabase
        .from('contratos')
        .select(`
          id, 
          created_at,
          status,
          consultor_id,
          cliente_id,
          bem_id,
          grupo_id,
          created_by
        `)
        .eq('tenant_id', this.tenantId)
        .in('created_by', membrosDoTime)
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      if (contratosError) {
        console.error('👥 [SUPERVISOR STATS] Erro na query de contratos:', contratosError);
        throw contratosError;
      }

      const contratos = contratosData || [];

      // Buscar dados dos bens para calcular valor total
      const bemIds = [...new Set(contratos.map(c => c.bem_id).filter(Boolean))];
      let bensData: any[] = [];
      if (bemIds.length > 0) {
        const { data } = await supabase
          .from('bens')
          .select('id, preco_bem')
          .in('id', bemIds);
        bensData = data || [];
      }

      // Criar mapa de preços dos bens
      const precoBemMap = new Map();
      bensData.forEach(bem => {
        precoBemMap.set(bem.id, parseFloat(bem.preco_bem) || 0);
      });

      // Calcular métricas da equipe
      const novosContratos = contratos.length;
      const contratosAtivos = contratos.filter(c => c.status === 'ATIVO' || c.status === 'FINALIZADO').length;
      const valorTotal = contratos.reduce((sum, contrato) => {
        const precoBem = precoBemMap.get(contrato.bem_id) || 0;
        return sum + precoBem;
      }, 0);
      const ticketMedio = novosContratos > 0 ? valorTotal / novosContratos : 0;

      // Buscar clientes únicos da equipe
      const clientesUnicos = new Set(contratos.map(c => c.cliente_id).filter(Boolean));
      const clientesAtendidos = clientesUnicos.size;

      // Calcular taxa de conversão (contratos ativos / total de contratos)
      const taxaConversaoEquipe = novosContratos > 0 ? (contratosAtivos / novosContratos) * 100 : 0;

      // Buscar dados de performance da equipe
      const performanceEquipe = await this.calcularDesempenhoMensal(contratos);

      // Buscar distribuição por status
      const statusCount = contratos.reduce((acc, contrato) => {
        acc[contrato.status] = (acc[contrato.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const contratosPorStatus = Object.entries(statusCount).map(([status, value]) => ({
        name: status,
        value: value as number
      }));

      // Metas da equipe via desempenho_config
      const refDate = dataFim.split('T')[0];
      const desempenho = await buscarIndicadoresDesempenho(this.tenantId, 'SUPERVISOR', refDate);
      const metaFaturamento = desempenho.metaValor;
      const metaEquipeAlcancada = calcularAtingimento(valorTotal, metaFaturamento);
      const metaEquipe =
        ticketMedio > 0 && metaFaturamento > 0
          ? Math.ceil(metaFaturamento / ticketMedio)
          : novosContratos;

      // Calcular performance média (baseada na taxa de conversão)
      const performanceMedia = taxaConversaoEquipe;

      // Comissões da equipe com base na config de desempenho
      const comissoesPagas = calcularComissao(valorTotal, desempenho, metaEquipeAlcancada >= 100);

      // Buscar IDs numéricos da equipe para buscar agendamentos
      const consultorIds = await this.getConsultorIdsDoTimeSupervisor(userId);

      // Buscar agendamentos que:
      // 1. Ocorrem no período (data_agendamento)
      // 2. Foram criados no período (created_at) - Produtividade de agendamento
      // 3. Foram atualizados no período (updated_at) - Produtividade de confirmação/realização

      const queryBase = () => supabase
        .from('agendamentos')
        .select('id, status, data_agendamento');

      let querySched = queryBase().gte('data_agendamento', dataInicio).lte('data_agendamento', dataFim);
      let queryCreated = queryBase().gte('created_at', dataInicio).lte('created_at', dataFim);
      let queryUpdated = queryBase().gte('updated_at', dataInicio).lte('updated_at', dataFim);

      if (consultorIds.length > 0) {
        const filterStr = `lider_vendas_id.in.(${consultorIds.join(',')}),relacionador_id.in.(${consultorIds.join(',')})`;
        querySched = querySched.or(filterStr);
        queryCreated = queryCreated.or(filterStr);
        queryUpdated = queryUpdated.or(filterStr);
      } else {
        // Force empty result
        querySched = querySched.eq('id', -1);
        queryCreated = queryCreated.eq('id', -1);
        queryUpdated = queryUpdated.eq('id', -1);
      }

      const [resSched, resCreated, resUpdated] = await Promise.all([querySched, queryCreated, queryUpdated]);

      // Merge results and deduplicate by ID
      const allAgendamentosMap = new Map();
      const addToMap = (data: any[]) => {
        data?.forEach(item => allAgendamentosMap.set(item.id, item));
      };

      addToMap(resSched.data || []);
      addToMap(resCreated.data || []);
      addToMap(resUpdated.data || []);

      const agendamentos = Array.from(allAgendamentosMap.values());
      const agendamentosError = resSched.error || resCreated.error || resUpdated.error; // Simple error handling

      if (agendamentosError) {
        console.error('👥 [SUPERVISOR STATS] Erro na query de agendamentos:', agendamentosError);
      }

      const totalAgendamentos = agendamentos.length;
      const agendamentosPendentes = agendamentos.filter((a: any) => a.status === 'PENDENTE').length;
      const agendamentosConfirmados = agendamentos.filter((a: any) => a.status === 'CONFIRMADO').length;
      const agendamentosRealizados = agendamentos.filter((a: any) => a.status === 'REALIZADO').length;
      const agendamentosCancelados = agendamentos.filter((a: any) => a.status === 'CANCELADO').length;

      // Taxa de comparecimento: (Realizados / (Realizados + Faltou)) * 100 OU (Realizados / Total que deveria ter ocorrido)
      // Considerando que "FALTOU" é um status possível
      const agendamentosFaltou = agendamentos.filter((a: any) => a.status === 'FALTOU').length;
      const totalAgendadosParaOcorrer = agendamentosRealizados + agendamentosFaltou;
      const taxaComparecimento = totalAgendadosParaOcorrer > 0
        ? (agendamentosRealizados / totalAgendadosParaOcorrer) * 100
        : 0;

      // ─── Detalhamento por membro da equipe ────────────────────────────────
      // Buscar nomes dos consultores para o ranking
      const { data: consultoresNomes } = await supabase
        .from('consultores')
        .select('id, nome, user_id')
        .in('user_id', membrosDoTime);

      const consultorNomeMap = new Map<string, string>();
      (consultoresNomes || []).forEach(c => {
        if (c.user_id) consultorNomeMap.set(c.user_id, c.nome || 'Membro');
      });

      // Agrupar contratos por membro
      const contratosPorMembro = new Map<string, { contratos: number; faturamento: number }>();
      membrosDoTime.forEach(uid => contratosPorMembro.set(uid, { contratos: 0, faturamento: 0 }));
      contratos.forEach(contrato => {
        const uid = contrato.created_by;
        if (!uid) return;
        const entry = contratosPorMembro.get(uid) || { contratos: 0, faturamento: 0 };
        entry.contratos += 1;
        entry.faturamento += precoBemMap.get(contrato.bem_id) || 0;
        contratosPorMembro.set(uid, entry);
      });

      // Agrupar agendamentos por membro
      // Buscar agendamentos com lider_vendas_id ou relacionador_id para mapear ao user_id
      const agendamentosPorMembro = new Map<string, {
        total: number; pendentes: number; confirmados: number;
        realizados: number; cancelados: number; faltou: number;
      }>();
      membrosDoTime.forEach(uid => agendamentosPorMembro.set(uid, {
        total: 0, pendentes: 0, confirmados: 0, realizados: 0, cancelados: 0, faltou: 0
      }));

      // Mapear consultor_id numérico → user_id para agendamentos
      const consultorIdToUserId = new Map<number, string>();
      (consultoresNomes || []).forEach(c => {
        if (c.user_id) consultorIdToUserId.set(c.id, c.user_id);
      });

      // Buscar agendamentos com consultor_id para poder mapear ao user
      const { data: agendamentosDetalhados } = await supabase
        .from('agendamentos')
        .select('id, status, lider_vendas_id, relacionador_id')
        .or(
          consultorIds.length > 0
            ? `lider_vendas_id.in.(${consultorIds.join(',')}),relacionador_id.in.(${consultorIds.join(',')})`
            : 'id.eq.-1'
        )
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim);

      (agendamentosDetalhados || []).forEach((ag: any) => {
        // Tentar mapear pelo lider_vendas_id ou relacionador_id
        const consultorNumId = ag.lider_vendas_id || ag.relacionador_id;
        const uid = consultorNumId ? consultorIdToUserId.get(consultorNumId) : undefined;
        if (!uid) return;

        const entry = agendamentosPorMembro.get(uid);
        if (!entry) return;

        entry.total += 1;
        if (ag.status === 'PENDENTE') entry.pendentes += 1;
        else if (ag.status === 'CONFIRMADO') entry.confirmados += 1;
        else if (ag.status === 'REALIZADO') entry.realizados += 1;
        else if (ag.status === 'CANCELADO') entry.cancelados += 1;
        else if (ag.status === 'FALTOU') entry.faltou += 1;
      });

      // Buscar ligações por membro (soma de ligacoes_realizadas dos clientes de cada membro)
      const { data: clientesLigacoes } = await supabase
        .from('clientes')
        .select('created_by, ligacoes_realizadas')
        .in('created_by', membrosDoTime)
        .eq('tenant_id', this.tenantId);

      // Agrupar ligações por membro
      const ligacoesPorMembro = new Map<string, number>();
      membrosDoTime.forEach(uid => ligacoesPorMembro.set(uid, 0));
      (clientesLigacoes || []).forEach((cliente: any) => {
        const uid = cliente.created_by;
        if (!uid) return;
        const atual = ligacoesPorMembro.get(uid) || 0;
        ligacoesPorMembro.set(uid, atual + (cliente.ligacoes_realizadas || 0));
      });

      const totalLigacoesEquipe = Array.from(ligacoesPorMembro.values()).reduce((sum, v) => sum + v, 0);

      // Montar array de membros da equipe
      const membrosDaEquipe = membrosDoTime.map(uid => {
        const c = contratosPorMembro.get(uid) || { contratos: 0, faturamento: 0 };
        const a = agendamentosPorMembro.get(uid) || {
          total: 0, pendentes: 0, confirmados: 0, realizados: 0, cancelados: 0, faltou: 0
        };
        const ligacoes = ligacoesPorMembro.get(uid) || 0;
        const taxaComparecimentoMembro = (a.realizados + a.faltou) > 0
          ? Math.round((a.realizados / (a.realizados + a.faltou)) * 100)
          : 0;
        // Taxa de conversão de ligações: agendamentos realizados / total de ligações
        const taxaConversaoLigacoes = ligacoes > 0
          ? Math.round((a.realizados / ligacoes) * 100)
          : 0;
        return {
          userId: uid,
          nome: consultorNomeMap.get(uid) || 'Membro',
          contratos: c.contratos,
          faturamento: c.faturamento,
          ticketMedio: c.contratos > 0 ? c.faturamento / c.contratos : 0,
          taxaConversao: c.contratos > 0
            ? Math.round((contratos.filter(ct => ct.created_by === uid && (ct.status === 'ATIVO' || ct.status === 'FINALIZADO')).length / c.contratos) * 100)
            : 0,
          ligacoes,
          taxaConversaoLigacoes,
          agendamentos: {
            ...a,
            taxaComparecimento: taxaComparecimentoMembro
          }
        };
      });
      // ──────────────────────────────────────────────────────────────────────

      return {
        equipeAtiva: membrosDoTime.length,
        novosContratos,
        metaEquipeAlcancada: Math.min(metaEquipeAlcancada, 100),
        performanceMedia: Math.round(performanceMedia * 100) / 100,
        faturamentoTotal: valorTotal,
        comissoesPagas,
        ticketMedio,
        taxaConversaoEquipe: Math.round(taxaConversaoEquipe * 100) / 100,
        clientesAtendidos,
        contratosEquipe: novosContratos,
        metaEquipe,
        metaFaturamento,
        desempenho,
        totalContratos: novosContratos,
        totalLigacoesEquipe,
        performanceEquipe,
        contratosPorStatus,
        membrosDaEquipe,
        agendamentos: {
          total: totalAgendamentos,
          pendentes: agendamentosPendentes,
          confirmados: agendamentosConfirmados,
          realizados: agendamentosRealizados,
          cancelados: agendamentosCancelados,
          faltou: agendamentosFaltou,
          taxaComparecimento: Math.round(taxaComparecimento * 100) / 100
        }
      };
    } catch (error) {
      console.error('👥 [SUPERVISOR STATS] Erro ao buscar dados do supervisor:', error);
      throw error;
    }
  }

  /**
   * Busca membros da equipe de um supervisor
   */
  private async getMembrosDoTimeSupervisor(supervisorId?: string): Promise<string[]> {
    if (!supervisorId) return [];

    try {
      // Primeiro, buscar o consultor supervisor para obter seu ID numérico
      const { data: supervisorData, error: supervisorError } = await supabase
        .from('consultores')
        .select('id')
        .eq('user_id', supervisorId)
        .eq('tipo', 'supervisor')
        .single();

      if (supervisorError || !supervisorData) {
        console.error('👥 [SUPERVISOR TEAM] Supervisor não encontrado:', supervisorError);
        return [supervisorId]; // Retorna apenas o próprio supervisor
      }

      const supervisorConsultorId = supervisorData.id;

      // Buscar consultores que têm o supervisor como supervisor_id
      const { data: consultoresData, error } = await supabase
        .from('consultores')
        .select('id, user_id')
        .eq('supervisor_id', supervisorConsultorId)
        .eq('status', 'ativo');

      if (error) {
        console.error('👥 [SUPERVISOR TEAM] Erro ao buscar consultores:', error);
        return [supervisorId]; // Retorna apenas o próprio supervisor
      }

      // Retornar user_ids dos consultores + o próprio supervisor
      const membrosDoTime = consultoresData?.map(c => c.user_id).filter(Boolean) || [];
      membrosDoTime.push(supervisorId);


      return membrosDoTime;
    } catch (error) {
      console.error('👥 [SUPERVISOR TEAM] Erro ao buscar membros da equipe:', error);
      return [supervisorId]; // Retorna apenas o próprio supervisor
    }
  }

  /**
   * Busca IDs numéricos dos membros da equipe de um supervisor
   */
  private async getConsultorIdsDoTimeSupervisor(supervisorUserId?: string): Promise<number[]> {
    if (!supervisorUserId) return [];

    try {
      // Buscar o ID numérico do supervisor
      const { data: supervisorData, error: supervisorError } = await supabase
        .from('consultores')
        .select('id')
        .eq('user_id', supervisorUserId)
        .eq('tipo', 'supervisor')
        .single();

      if (supervisorError || !supervisorData) {
        console.error('👥 [SUPERVISOR TEAM] Supervisor não encontrado (IDs):', supervisorError);
        return [];
      }

      const supervisorConsultorId = supervisorData.id;

      // Buscar IDs dos consultores que têm o supervisor como supervisor_id
      const { data: consultoresData, error } = await supabase
        .from('consultores')
        .select('id')
        .eq('supervisor_id', supervisorConsultorId)
        .eq('status', 'ativo');

      if (error) {
        console.error('👥 [SUPERVISOR TEAM] Erro ao buscar consultores (IDs):', error);
        return [supervisorConsultorId]; // Retorna apenas o próprio supervisor se der erro
      }

      // Retornar IDs dos consultores + o próprio supervisor
      const ids = consultoresData?.map(c => c.id) || [];
      if (!ids.includes(supervisorConsultorId)) {
        ids.push(supervisorConsultorId);
      }


      return ids;
    } catch (error) {
      console.error('👥 [SUPERVISOR TEAM] Erro ao buscar IDs membros da equipe:', error);
      return [];
    }
  }
}

export const supabaseDashboardService = new SupabaseDashboardService();